import { useParams } from "react-router-dom";
import {
  useApproveTicketMutation,
  useGetTicketLogDetailsQuery,
  useRejectTicketMutation,
  useResolveTicketMutation,
} from "../../../../../features/superAdmin/ticketService/ticket/ticket.api";
import SidebarLayout from "../../../../../layouts/SidebarLayout";

import { Button, Input } from "antd";
import { useEffect, useState } from "react";
import { useAppSelector } from "../../../../../app/store";
import ActionModal from "../../../../../components/ui/ActionModal";
import {
  TicketDetails as TicketDetailsType,
  TicketWorkflow,
} from "../../../../../features/superAdmin/ticketService/ticket/ticket.type";
import useError from "../../../../../hooks/useError";
import useModal from "../../../../../hooks/useModal";
import {
  convertMinutesToSeconds,
  getSecondsDiff,
} from "../../../../../lib/time";
import { toast } from "../../../../../lib/toast";
import { TEMPLATE_TYPES } from "../../constants/ticket.constants";
import DetailsSpecification from "./_components/DetailsSpecification";
import ForwardOpenTicketModal from "./_components/ForwardOpenTicketModal";
import TicketDetailsHeader from "./_components/TicketDetailsHeader";
import TicketDetailsTabs from "./_components/TicketDetailsTabs";
import TicketDetailsTitleDesc from "./_components/TicketDetailsTitleDesc";

const getActiveRole = (
  workflows: TicketWorkflow[] = [],
  currentUserId: number,
  validRoles: TicketWorkflow["role_type"][] = [
    "checker",
    "executor",
    "approver",
  ],
  type: string,
): string | undefined => {
  const workflow = workflows.find((wf) => {
    const {
      updated_at,
      status,
      start_time,
      visibility_duration_minutes,
      employee_id,
      role_type,
      bypass,
    } = wf;

    if (employee_id !== currentUserId) return false;
    if (!validRoles.includes(role_type)) return false;
    if (status !== "pending") return false;
    if (type !== TEMPLATE_TYPES.workflow) return false;

    const visibilityInSeconds = convertMinutesToSeconds(
      visibility_duration_minutes || 0,
    );
    const referenceTime =
      status === "pending" ? new Date().toISOString() : updated_at;
    const elapsedSeconds = getSecondsDiff(referenceTime, start_time);

    return bypass ? elapsedSeconds < visibilityInSeconds : true;
  });

  return workflow?.role_type;
};

const getUserWorkflowStatus = (
  workflows: TicketWorkflow[] = [],
  currentUserId: number,
): "Checked" | "Approved" | "Executed" | "Rejected" | null => {
  const wf = workflows.find(
    (item) =>
      item.employee_id === currentUserId &&
      (item.role_type === "checker" ||
        item.role_type === "executor" ||
        item.role_type === "approver") &&
      (item.status === "done" || item.status === "rejected"),
  );

  if (!wf) return null;

  if (wf.status === "rejected") return "Rejected";

  switch (wf.role_type) {
    case "checker":
      return "Checked";
    case "approver":
      return "Approved";
    case "executor":
      return "Executed";
    default:
      return null;
  }
};

const ROLE_CONFIG = {
  checker: "Check",
  executor: "Execute",
  approver: "Approve",
  rejector: "Reject",
  resolver: "Resolve",
  forwarder: "Forward",
};

const ApproverTicketDetails = () => {
  const { id } = useParams();
  const { user } = useAppSelector((state) => state.auth);
  const { open, handleOpen, handleClose } = useModal<
    { role: string },
    "ACTION" | "FORWARD"
  >();

  const { data: detailsResponse } = useGetTicketLogDetailsQuery({
    params: { id: id as string, emp_id: "" },
  });

  const ticketDetails = (detailsResponse?.data || {}) as TicketDetailsType;
  const { workflows, type, status } = ticketDetails;

  // Constants for workflow based ticket action
  const activeRole = getActiveRole(
    workflows,
    user?.id as number,
    ["checker", "executor", "approver"],
    type,
  );
  const selectedRole = open.data?.role;
  const roleLabel = ROLE_CONFIG?.[selectedRole as keyof typeof ROLE_CONFIG];
  const approveStatus = getUserWorkflowStatus(workflows, user?.id as number);

  // Constants for sla based ticket action
  const currentChecker = workflows?.find((workflow: TicketWorkflow) => {
    return (
      workflow.employee?.officialInfo?.department_id === user?.department_id &&
      workflow.status === "pending" &&
      !workflow.is_forwarded &&
      !workflow.is_resolved
    );
  });

  const canActOpenTicket =
    !!currentChecker &&
    type === TEMPLATE_TYPES.sla &&
    status !== "closed" &&
    status !== "canceled";

  // Component States
  const [comment, setComment] = useState<string>("");

  // Mutations
  const [
    approveTicket,
    {
      isLoading: approving,
      isSuccess: isApproved,
      isError: isApproveError,
      error: approveError,
    },
  ] = useApproveTicketMutation();
  const [
    rejectTicket,
    {
      isLoading: rejecting,
      isSuccess: isRejected,
      isError: isRejectError,
      error: rejectError,
    },
  ] = useRejectTicketMutation();
  const [
    resolveTicket,
    {
      isSuccess: isResolved,
      isLoading: resolving,
      isError: isResolveError,
      error: resolveError,
    },
  ] = useResolveTicketMutation();

  // Handler Functions
  const handleAction = () => {
    if (selectedRole === "resolver") {
      resolveTicket({ id: ticketDetails?.id });
      return;
    }
    if (!comment || comment === "") {
      toast("error", "Please add a comment before proceeding.");
      return null;
    }

    if (selectedRole === "rejector") {
      rejectTicket({
        id: ticketDetails?.id,
        data: {
          userId: user?.id,
          note: comment.trim(),
        },
      });
    } else if (
      selectedRole === "checker" ||
      selectedRole === "approver" ||
      selectedRole === "executor"
    ) {
      approveTicket({
        id: ticketDetails?.id,
        data: {
          userId: user?.id,
          note: comment.trim(),
        },
      });
    }
  };

  // Effects
  useEffect(() => {
    if (isRejected) {
      handleClose();
      toast("success", "Ticket rejected.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRejected]);

  useEffect(() => {
    if (isApproved) {
      handleClose();
      toast("success", `Ticket ${roleLabel}ed.`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isApproved]);

  useEffect(() => {
    if (isResolved) {
      handleClose();
      toast("success", `Ticket Resolved.`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResolved]);

  // Errors
  useError(isApproveError, approveError, `Ticket ${roleLabel} failed!`);
  useError(isRejectError, rejectError, "Ticket Reject failed!");
  useError(isResolveError, resolveError, "Ticket Resolve failed!");

  return (
    <SidebarLayout title="Ticket Management">
      <TicketDetailsHeader>
        <div className="flex items-center gap-4">
          {canActOpenTicket && (
            <>
              <Button
                onClick={() => {
                  handleOpen({ type: "ACTION", data: { role: "resolver" } });
                }}
                className="button-primary-light h-10"
              >
                Resolve
              </Button>
              <Button
                onClick={() => {
                  handleOpen({ type: "FORWARD", data: { role: "forwarder" } });
                }}
                className="button-primary-light h-10"
              >
                Forward
              </Button>
            </>
          )}
          {!!(activeRole === "approver") &&
            status !== "closed" &&
            status !== "canceled" && (
              <Button
                onClick={() => {
                  handleOpen({ type: "ACTION", data: { role: "rejector" } });
                }}
                className="button-bordered-light h-10"
              >
                Reject
              </Button>
            )}
          {!!activeRole && status !== "closed" && status !== "canceled" && (
            <Button
              onClick={() => {
                handleOpen({ type: "ACTION", data: { role: activeRole } });
              }}
              className="button-primary-light h-10"
            >
              {ROLE_CONFIG?.[activeRole as keyof typeof ROLE_CONFIG]}
            </Button>
          )}
        </div>
      </TicketDetailsHeader>
      <div className="grid grid-cols-5 gap-4">
        <div className="col-span-3">
          <TicketDetailsTitleDesc ticket={ticketDetails} />
          <DetailsSpecification ticket={ticketDetails} />
        </div>
        <div className="col-span-2">
          <TicketDetailsTabs ticketDetails={ticketDetails} />
        </div>
      </div>
      {!!approveStatus && (
        <div className="flex items-center justify-center mt-5">
          <Button className="button-bordered-gray" disabled>
            {approveStatus}
          </Button>
        </div>
      )}

      {!!(open.type === "ACTION") && (
        <ActionModal
          title={`Do you want to ${roleLabel} the ticket?`}
          subTitle={`Are you sure you want to ${roleLabel} this ticket? This action cannot be undone.`}
          open={open?.open}
          actionBtnText={roleLabel}
          actionBtnType={
            open?.data?.role === "rejector" ? "danger-light" : "primary-light"
          }
          handleClose={handleClose}
          handleCancel={handleClose}
          handleAction={handleAction}
          iconType={open?.data?.role === "rejector" ? "danger" : "warning"}
          loading={approving || rejecting || resolving}
        >
          {!!(selectedRole !== "resolver" && selectedRole !== "forwarder") && (
            <Input
              className="h-10"
              type="text"
              placeholder="Add comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          )}
        </ActionModal>
      )}
      {open.type === "FORWARD" && (
        <ForwardOpenTicketModal
          raiserId={ticketDetails?.raised_by as number}
          id={ticketDetails.id}
          open={open.open}
          handleClose={handleClose}
        />
      )}
    </SidebarLayout>
  );
};

export default ApproverTicketDetails;
